import React, { useState, useEffect, memo, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, TextField, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Snackbar, IconButton } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import QrCodeIcon from '@mui/icons-material/QrCode';
import FolderIcon from '@mui/icons-material/Folder';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { QRCodeSVG } from 'qrcode.react';
import { VBID, ShareableVBID } from '../types/vbid';
import { NumberKeyState } from '../types';
import { generateShareUrl } from '../services/shareService';
import { useNavigate } from 'react-router-dom';
import { createVBIDSnapshot } from '../services/vbidSnapshotService';
import { createShortCodeForSnapshot } from '../services/shortUrlService';
import { useAuth } from '../contexts/AuthContext';
import { collection, addDoc, serverTimestamp, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { youtubeApi } from '../services/youtubeApi';
import { formatTimecode } from '../utils/vbidUtils';
import { convertNumberKeysToVBIDs } from '../utils/vbidUtils';
import { vbidsToNumberKeys } from '../utils/numberKeyUtils';

console.log('VbidDialog loaded');

interface VbidDialogProps {
  open: boolean;
  onClose: () => void;
  vbids: ShareableVBID[];
  onImport?: (importedVbids: ShareableVBID[]) => void;
  currentMode?: string;
  videoUrl?: string;
  numberKeys?: any;
  abPoints?: any;
  currentVbids?: any[];
  currentTime?: number;
  duration?: number;
  playing?: boolean;
  volume?: number;
  muted?: boolean;
  playbackRate?: number;
  played?: number;
  loaded?: number;
  seeking?: boolean;
  showControls?: boolean;
  showNumberKeyPanel?: boolean;
  showABPointPanel?: boolean;
  showVBIDPanel?: boolean;
  showImageEditor?: boolean;
  imageUrl?: string;
  selectedFontSize?: number;
  lineWidthMap?: any;
  history?: any[];
  historyIndex?: number;
  onSave?: () => void;
  onMyFavorite?: () => void;
  playButtonSound?: (soundType: 'click' | 'delete' | 'reset' | 'shutter') => void;
  filterDuplicateVBIDs?: (vbids: VBID[]) => VBID[];
  setTabVbids?: (vbids: VBID[]) => void;
  tabValue?: number;
  mButtonState?: string;
}

const VbidDialog: React.FC<VbidDialogProps> = memo(({
  open,
  vbids = [],
  onImport,
  currentMode = 'YouTube',
  videoUrl = '',
  numberKeys = {},
  abPoints = { pointA: { key: null, timestamp: null }, pointB: { key: null, timestamp: null } },
  currentVbids = [],
  currentTime = 0,
  duration = 0,
  playing = false,
  volume = 1,
  muted = false,
  playbackRate = 1,
  played = 0,
  loaded = 0,
  seeking = false,
  showControls = true,
  showNumberKeyPanel = false,
  showABPointPanel = false,
  showVBIDPanel = false,
  showImageEditor = false,
  imageUrl = '',
  selectedFontSize = 24,
  lineWidthMap = {},
  history = [],
  historyIndex = -1,
  onClose,
  onSave,
  onMyFavorite,
  playButtonSound,
  filterDuplicateVBIDs,
  setTabVbids,
  tabValue = 3,
  mButtonState = 'M'
}) => {
  if (!onClose) {
    console.error('onClose 函數未提供');
  }

  const [showQRCode, setShowQRCode] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    // 移除 onMyFavorite 相關的 console.log
  }, [onMyFavorite]);

  const safeVbids = Array.isArray(vbids) ? vbids : [];
  const hasVbids = safeVbids.length > 0;

  const handleExportCheckpoint = () => {
    if (!hasVbids) {
      console.error('沒有可匯出的 Check Points');
      return;
    }

    console.log('匯出前 safeVbids:', safeVbids);

    try {
      const exportData = {
        source: safeVbids[0]?.mediaMode || 'YouTube',
        path: safeVbids[0]?.path || '',
        timestamp: new Date().toISOString(),
        checkpoints: safeVbids.map(vbid => {
          const [number, time] = vbid.timecode.split('_');
          return {
            checkpointNumber: number,
            timecode: time,
            note: vbid.note || '',
            screenshot: vbid.screenshot || ''
          };
        })
      };

      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `checkpoints-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('成功匯出 Check Points');
    } catch (error) {
      console.error('Error exporting checkpoints:', error);
      console.error('匯出 Check Points 失敗');
    }
  };

  const handleGenerateQRCode = async () => {
    if (!hasVbids) {
      console.error('沒有可分享的 Check Points');
      return;
    }
    if (!user || !user.uid) {
      alert('請先登入才能分享！');
      return;
    }
    try {
      // 將 vbids 轉換為 snapshot 結構，包含 keyNo
      const vbidsForSnapshot = safeVbids.reduce((acc, vbid, idx) => {
        acc[idx] = {
          keyNo: Number(vbid.keyNo ?? idx),
          timecode: vbid.timecode,
          note: vbid.note,
          screenshot: vbid.screenshot,
          flag: vbid.flag,
          isActive: vbid.flag === 'on',
          timestamp: 0 // 若有 timestamp 可補上
        };
        return acc;
      }, {} as Record<string, any>);
      const metadata = {
        mode: safeVbids[0]?.mediaMode || 'YouTube',
        path: safeVbids[0]?.path || ''
      };
      // 建立快照
      const snapshotId = await createVBIDSnapshot(vbidsForSnapshot, metadata, user.uid, '', '');
      // 建立短碼
      const shortCode = await createShortCodeForSnapshot(snapshotId);
      const baseUrl = window.location.origin.replace('www.', '');
      const finalUrl = `${baseUrl}/v/${shortCode}`;
      console.log('snapshotId:', snapshotId);
      console.log('shortCode:', shortCode);
      console.log('finalUrl:', finalUrl);
      setShareUrl(finalUrl);
      setShowQRCode(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
      alert('產生分享連結失敗，請稍後再試！');
    }
  };

  const handleMyFavorite = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止事件冒泡
    if (onMyFavorite) {
      onMyFavorite(); // 呼叫父元件傳入的 onMyFavorite 函數
    }
  }, [onMyFavorite]);

  // 確保所有必要的 props 都有值
  const safeProps = {
    currentMode: currentMode || 'VIDEO',
    videoUrl: videoUrl || '',
    numberKeys: numberKeys || {},
    abPoints: abPoints || { pointA: { key: null, timestamp: null }, pointB: { key: null, timestamp: null } },
    currentVbids: currentVbids || [],
    currentTime: currentTime || 0,
    duration: duration || 0,
    playing: playing || false,
    volume: volume || 1,
    muted: muted || false,
    playbackRate: playbackRate || 1,
    played: played || 0,
    loaded: loaded || 0,
    seeking: seeking || false,
    showControls: showControls || true,
    showNumberKeyPanel: showNumberKeyPanel || false,
    showABPointPanel: showABPointPanel || false,
    showVBIDPanel: showVBIDPanel || false,
    showImageEditor: showImageEditor || false,
    imageUrl: imageUrl || '',
    selectedFontSize: selectedFontSize || 24,
    lineWidthMap: lineWidthMap || {},
    history: history || [],
    historyIndex: historyIndex || -1
  };

  // 使用安全的 props
  const {
    currentMode: safeCurrentMode,
    videoUrl: safeVideoUrl,
    numberKeys: safeNumberKeys,
    abPoints: safeAbPoints,
    currentVbids: safeCurrentVbids,
    currentTime: safeCurrentTime,
    duration: safeDuration,
    playing: safePlaying,
    volume: safeVolume,
    muted: safeMuted,
    playbackRate: safePlaybackRate,
    played: safePlayed,
    loaded: safeLoaded,
    seeking: safeSeeking,
    showControls: safeShowControls,
    showNumberKeyPanel: safeShowNumberKeyPanel,
    showABPointPanel: safeShowABPointPanel,
    showVBIDPanel: safeShowVBIDPanel,
    showImageEditor: safeShowImageEditor,
    imageUrl: safeImageUrl,
    selectedFontSize: safeSelectedFontSize,
    lineWidthMap: safeLineWidthMap,
    history: safeHistory,
    historyIndex: safeHistoryIndex
  } = safeProps;

  const handleBackToHome = () => {
    const savedState = localStorage.getItem('mainPageStateBackup') || sessionStorage.getItem('mainPageState');
    if (savedState) {
      const state = JSON.parse(savedState);
      navigate('/', { state: { ...state, returnFromFavorites: true, timestamp: Date.now() }, replace: true });
    }
  };

  // 固定顯示所有常用欄位
  const columns = [
    'keyNo', 'timecode', 'note', 'screenshot', 'flag', 'timestamp', 'isActive', 'mediaMode', 'playMode', 'path', 'userId'
  ];

  function generateShareUrl() {
    // 這裡根據你的需求組合網址
    // 例如：將 VBID 資訊 encode 成 base64 放在網址參數
    const data = {
      vbids: safeVbids,
      path: videoUrl,
      mode: currentMode,
    };
    const encoded = btoa(encodeURIComponent(JSON.stringify(data)));
    return `${window.location.origin.replace('www.', '')}/share?v=${encoded}`;
  }

  const handleShareClick = async () => {
    // 移除登入檢查，允許未登入分享，2025/05/23
    // if (!user?.email) {
    //   alert('請先登入後再分享');
    //   return;
    // }

    setLoading(true);
    try {
      // 優先用 currentVbids，有資料就用，否則 fallback 用 vbids
      const dataSource = (currentVbids && currentVbids.length > 0) ? currentVbids : vbids;
      if (!dataSource || dataSource.length === 0) {
        throw new Error('沒有可分享的資料');
      }

      // 只保留 Firestore 允許的欄位，並確保資料格式正確
      const cleanVbids = dataSource.map(vbid => {
        const keyNo = parseInt(vbid.keyNo || vbid.key || '', 10);
        if (isNaN(keyNo)) {
          throw new Error('無效的標記編號');
        }
        return {
          keyNo: Number(vbid.keyNo), // 保證是 number
          timecode: vbid.timecode || '',
          note: vbid.note || '',
          screenshot: vbid.screenshot || '',
          flag: vbid.flag || 'on',
          timestamp: Date.now(),
          frame: vbid.frame || 0, // <--- 新增 frame 欄位
          mediaMode: vbid.mediaMode || 'Unknown',
          playMode: vbid.playMode || 'M'
        };
      });

      // 取第一筆的 path，如果沒有就用 videoUrl
      let pathValue = (dataSource[0] && dataSource[0].path) ? dataSource[0].path : (videoUrl || '');
      if (!pathValue) {
        throw new Error('缺少視頻路徑');
      }

      // numberKeys fallback
      let numberKeysToSave = numberKeys;
      if (!numberKeysToSave || Object.keys(numberKeysToSave).length === 0) {
        numberKeysToSave = vbidsToNumberKeys(dataSource);
        console.log('🔄 numberKeys 為空，自動用 vbids 產生:', numberKeysToSave);
      }

      const dataToSave = {
        userId: user?.uid || 'guest', // 未登入者以 guest 身份分享
        addedDate: new Date().toISOString(),
        mode: currentMode || 'YouTube',
        path: pathValue,
        privacy: 'Public',
        vbids: cleanVbids,
        numberKeys: numberKeysToSave,
        playMode: mButtonState
      };

      console.log('即將送出的資料：', JSON.stringify(dataToSave, null, 2));
      console.log('numberKeys 內容：', JSON.stringify(numberKeysToSave, null, 2));
      Object.values(numberKeysToSave).forEach((nk: any, idx) => {
        console.log(`numberKey[${idx}]: videoMode=`, nk.videoMode, ', playMode=', nk.playMode ?? 'M');
      });

      // 使用 try-catch 包裝 Firestore 操作
      let docRef;
      try {
        // 🔧 修復：使用統一的文檔 ID 生成策略
        const videoId = youtubeApi.extractVideoId(pathValue);
        
        // 生成基於檔案名稱的文檔ID（如果沒有videoId）
        let documentId;
        if (videoId) {
          documentId = videoId;
        } else {
          // 從路徑中提取檔案名稱
          const pathParts = pathValue.split('/');
          const fileName = pathParts[pathParts.length - 1] || 'unknown_file';
          // 移除查詢參數
          const cleanFileName = fileName.split('?')[0];
          // 生成基於檔案名稱的ID
          const cleanName = cleanFileName.replace(/[^a-zA-Z0-9\u4e00-\u9fff.-]/g, '_').toLowerCase();
          documentId = `file_${cleanName}`;
        }
        
        // 檢查是否已存在相同文檔
        const existingDocRef = doc(db, 'sharedVbids', documentId);
        const existingDocSnap = await getDoc(existingDocRef);
        
        if (existingDocSnap.exists()) {
          // 如果文檔已存在，更新它
          await updateDoc(existingDocRef, dataToSave);
          docRef = existingDocRef;
          console.log('Firestore 更新成功，文件 ID:', documentId);
        } else {
          // 如果文檔不存在，建立新文檔
          await setDoc(existingDocRef, dataToSave);
          docRef = existingDocRef;
          console.log('Firestore 寫入成功，文件 ID:', documentId);
        }
        
        // *** 新增：初始化 videoStats 關注度為 1 ***
        if (videoId && videoId.match(/^[a-zA-Z0-9_-]{11}$/)) {
          try {
            const videoStatsRef = doc(db, 'videoStats', videoId);
            const videoStatsSnap = await getDoc(videoStatsRef);
            
            if (!videoStatsSnap.exists()) {
              // 如果 videoStats 不存在，創建並設定關注度為 1
              await setDoc(videoStatsRef, {
                attentionCount: 1,
                userAttention: { [user?.email || 'guest']: 1 },
                createdAt: new Date().toISOString()
              });
              // console.log('✅ 已初始化 videoStats 關注度為 1:', videoId);
            }
          } catch (e) {
            console.warn('❌ 初始化 videoStats 失敗:', e);
          }
        }
      } catch (firestoreError) {
        console.error('Firestore 寫入失敗:', firestoreError);
        throw new Error('資料儲存失敗，請稍後再試');
      }

      const url = `${window.location.origin.replace('www.', '')}/share/${docRef.id}`;
      setShareUrl(url);

      setShowShareDialog(true);
    } catch (error) {
      console.error('Share failed:', error);
      alert('分享失敗：' + (error instanceof Error ? error.message : '未知錯誤'));
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (!open) setLoading(false);
  }, [open]);
  
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            backgroundColor: '#fff7e6',
            p: { xs: 1, sm: 2 },
            mx: { xs: 0.5, sm: 'auto' },
            width: { xs: '98vw', sm: 'auto' },
            maxWidth: { xs: '98vw', sm: 700 },
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <DialogTitle id="vbid-dialog-title">
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              pt: 1
            }}>
              <Typography variant="h5" sx={{ 
                color: '#000000', 
                mb: 0.75,
                fontWeight: 'bold',
                letterSpacing: '0.1em'
              }}>
                立即與朋友分享你的
              </Typography>
              <Typography variant="h5" sx={{ 
                color: '#000000', 
                fontWeight: 'bold',
                letterSpacing: '0.1em',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5
              }}>
                #<Box component="span" sx={{ color: '#00B0FF' }}>Check</Box><Box component="span" sx={{ color: '#FF0000' }}>Point</Box>
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 , gap: 2, justifyContent: 'space-between' }}>
              <Box sx={{ 
                '& .label': {
                  marginRight: '12px',
                  display: 'inline-block',
                  color: '#000000'
                },
                '& .mode': {
                  color: '#888888'
                },
                '& .path': {
                  color: '#888888'
                }
              }}>
                <Box sx={{ mb: 1 }}>
                  <Typography component="span" className="label">來源：</Typography>
                  <Typography component="span" className="mode">
                    {(() => {
                      const mediaMode = safeVbids[0]?.mediaMode;
                      if (!mediaMode || mediaMode === 'Unknown') return '未知來源';
                      if (mediaMode === 'YouTube') return 'YouTube';
                      if (mediaMode === 'Google') return 'Google 雲端';
                      if (mediaMode === 'VIDEO') return '本地影片';
                      if (mediaMode === 'Short') return '短影片';
                      if (mediaMode === 'PHOTO') return '照片';
                      if (mediaMode === 'PDF') return 'PDF 文件';
                      if (mediaMode === 'Multiview') return '多視窗模式';
                      return mediaMode;
                    })()}
                  </Typography>
                </Box>
                <Box>
                  <Typography component="span" className="label">Path：</Typography>
                  <Typography component="span" className="path">{safeVbids[0]?.path || '-'}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleMyFavorite}
                  disabled={!user}
                  sx={{
                    backgroundColor: '#FF8C00',
                    '&:hover': {
                      backgroundColor: '#FF7000'
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#cccccc !important',
                      color: '#666666 !important',
                      boxShadow: 'none !important',
                      opacity: 0.7,
                      pointerEvents: 'none'
                    }
                  }}
                  startIcon={<FolderIcon />}
                >
                影片管理(V)
                </Button>
              </Box>
            </Box>
            <TableContainer sx={{ 
              maxHeight: 440,
              backgroundColor: '#fff2cc',
              '& .MuiTable-root': {
                backgroundColor: '#fff2cc'
              },
              '& .MuiTableCell-root': {
                borderColor: '#e6b800'
              },
              '& .MuiTableRow-root:hover': {
                backgroundColor: '#ffe680 !important'
              }
            }}>
              <Table aria-label="視頻書籤列表">
                <TableHead>
                  <TableRow>
                    {columns.map(col => (
                      <TableCell key={col}>{col}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <>
                    {safeVbids.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" sx={{ py: 2, color: '#666666' }}>
                            尚未設定任何 Check Points
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) :
                      safeVbids.map((vbid, idx) => (
                        <TableRow key={vbid.keyNo ?? idx}>
                          <TableCell key="keyNo">
                            {vbid.keyNo ?? (typeof vbid.timecode === 'string' && vbid.timecode.includes('_') ? vbid.timecode.split('_')[0] : idx + 1)}
                          </TableCell>
                          <TableCell key="timecode">
                            {typeof vbid.timecode === 'string' && vbid.timecode.includes('_') ? vbid.timecode.split('_')[1] : vbid.timecode}
                          </TableCell>
                          {columns.slice(2).map(col => (
                            <TableCell key={col}>
                              {typeof (vbid as any)[col] === 'boolean'
                                ? (vbid as any)[col] ? 'true' : 'false'
                                : (vbid as any)[col] ?? '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    }
                  </>
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = '.json';
                  input.onchange = (e) => {
                    const file = (e.target as HTMLInputElement).files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        try {
                          const importData = JSON.parse(event.target?.result as string);
                          if (importData && importData.checkpoints && Array.isArray(importData.checkpoints)) {
                            const convertedVbids = importData.checkpoints.map((cp: any) => ({
                              mode: importData.source || 'YouTube',
                              timecode: formatTimecode(Number(cp.timeInSeconds), Number(cp.frame)),
                              note: cp.note || '',
                              screenshot: cp.screenshot || '',
                              path: importData.path || '',
                              flag: 'on' as const,
                              privacy: 'Private' as const,
                              userId: ''
                            }));
                            
                            if (onImport) {
                              const vbidsWithPath = convertedVbids.map((vbid: ShareableVBID) => ({
                                ...vbid,
                                path: importData.path || ''
                              }));
                              onImport(vbidsWithPath);
                            }
                            
                            console.log('成功匯入 Check Points');
                          } else {
                            throw new Error('Invalid data format');
                          }
                        } catch (error) {
                          console.error('Import failed:', error);
                          console.error('匯入失敗，請確認檔案格式');
                        }
                      };
                      reader.readAsText(file);
                    }
                  };
                  input.click();
                }}
                sx={{
                  backgroundColor: '#ff4444',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#cc0000'
                  }
                }}
              >
                匯入
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleExportCheckpoint}
                disabled={safeVbids.length === 0}
                sx={{
                  backgroundColor: '#4CAF50',
                  '&:hover': {
                    backgroundColor: '#45a049'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#cccccc !important',
                    color: '#666666 !important',
                    boxShadow: 'none !important',
                    opacity: 0.7,
                    pointerEvents: 'none'
                  }
                }}
              >
                匯出
              </Button>
              <Button
                variant="contained"
                color="info"
                onClick={handleShareClick}
                disabled={loading}
                  startIcon={<ShareIcon />}
              >
                分享
              </Button>
            </Box>
            
            <Button 
              onClick={onClose}
              sx={{
                fontSize: '1rem',
                fontWeight: 300
              }}
            >
              CLOSE
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={showQRCode}
        onClose={() => setShowQRCode(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 2
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h6" align="center">
            分享 QR Code
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 2,
            p: 2
          }}>
            {shareUrl && (
            <QRCodeSVG
              value={shareUrl}
                size={180}
              level="M"
              includeMargin={true}
              style={{
                  padding: '8px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}
            />
            )}
            <Typography variant="body2" color="text.secondary" align="center">
              掃描 QR Code 以分享你的 Check Points
            </Typography>
            {shareUrl && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: 2,
                background: '#f5f7fa',
                borderRadius: '8px',
                px: 2,
                py: 1,
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
              }}>
                <Typography
                  variant="body1"
                  color="primary"
                  sx={{
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    letterSpacing: '0.02em',
                    mr: 1,
                    wordBreak: 'break-all',
                    userSelect: 'all',
                    textAlign: 'center'
                  }}
                >
                  {shareUrl}
                </Typography>
                <IconButton
                  size="small"
                  aria-label="複製短網址"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(shareUrl);
                      setCopySuccess(true);
                    } catch (e) {
                      setCopySuccess(false);
                    }
                  }}
                  sx={{ ml: 0.5 }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowQRCode(false)}>關閉</Button>
        </DialogActions>
        <Snackbar
          open={copySuccess}
          autoHideDuration={1800}
          onClose={() => setCopySuccess(false)}
          message="已複製短網址！"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </Dialog>

      <Dialog open={showShareDialog} onClose={() => setShowShareDialog(false)} maxWidth="xs" PaperProps={{ sx: { borderRadius: 3, p: 2 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShareIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            分享連結
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* 直接顯示 QR Code */}
          {shareUrl && (
            <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <QRCodeSVG
                value={shareUrl}
                size={180}
                level="M"
                includeMargin={true}
                style={{
                  padding: '8px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              />
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                掃描 QR Code 以分享你的 Check Points
              </Typography>
            </Box>
          )}
          {/* 分享網址在下方 */}
          <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              分享網址
            </Typography>
            <Box
              sx={{
                background: '#f5f7fa',
                borderRadius: 1,
                p: 1,
                fontSize: '0.95rem',
                wordBreak: 'break-all',
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <span style={{ flex: 1 }}>{shareUrl}</span>
              <IconButton
                size="small"
                aria-label="複製分享網址"
                onClick={async () => {
                  await navigator.clipboard.writeText(shareUrl);
                  setCopySuccess(true);
                }}
                sx={{ ml: 1 }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
              點擊右側圖示可複製分享網址
            </Typography>
          {copySuccess && (
            <Alert severity="success" sx={{ mt: 1 }}>
                完成複製，登入CheckPlay管理影片重點！
            </Alert>
          )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowShareDialog(false)} variant="outlined" color="primary">
            關閉
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default VbidDialog; 